
import React from 'react';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Pencil, Trash2, Key } from 'lucide-react';

interface User {
  id: string;
  username: string;
  fullName: string;
  role: string;
  email: string | null;
  active: boolean;
}

interface StaffTableProps {
  users: User[];
  openEditDialog: (user: User) => void;
  openDeleteDialog: (user: User) => void;
  openResetPasswordDialog: (user: User) => void;
  currentUserId?: string;
}

const StaffTable: React.FC<StaffTableProps> = ({
  users,
  openEditDialog,
  openDeleteDialog,
  openResetPasswordDialog,
  currentUserId,
}) => {
  return (
    <Table>
      <TableCaption>List of users</TableCaption>
      <TableHeader>
        <TableRow>
          <TableHead>Username</TableHead>
          <TableHead>Full Name</TableHead>
          <TableHead>Role</TableHead>
          <TableHead>Email</TableHead>
          <TableHead>Status</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {users.length > 0 ? (
          users.map((user: User) => (
            <TableRow key={user.id}>
              <TableCell className="font-medium">{user.username}</TableCell>
              <TableCell>{user.fullName}</TableCell>
              <TableCell>
                <Badge variant={user.role === 'admin' ? 'default' : user.role === 'staff' ? 'secondary' : 'outline'}>
                  {user.role}
                </Badge>
              </TableCell>
              <TableCell>{user.email || '-'}</TableCell>
              <TableCell>
                <Badge variant={user.active ? 'secondary' : 'destructive'}>
                  {user.active ? 'Active' : 'Inactive'}
                </Badge>
              </TableCell>
              <TableCell className="text-right space-x-2">
                <Button size="sm" variant="outline" onClick={() => openEditDialog(user)}>
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button size="sm" variant="outline" onClick={() => openResetPasswordDialog(user)}>
                  <Key className="h-4 w-4" />
                </Button>
                {/* Prevent deletion of current user */}
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="text-red-500" 
                  onClick={() => openDeleteDialog(user)}
                  disabled={user.id === currentUserId}
                  title={user.id === currentUserId ? "Cannot delete your own account" : "Delete user"}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </TableCell>
            </TableRow>
          ))
        ) : (
          <TableRow>
            <TableCell colSpan={6} className="text-center">
              No users found. Add your first user or check the database connection.
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
};

export default StaffTable;
